import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { conciergeReviewAPI, intelligenceMe, errMsg, fmtDate, usd } from '../services/intelligenceApi';
import { RoleChips } from './IntelligenceKb';
import './Intelligence.css';

/* INTELLIGENCE → CONCIERGE REVIEW (brain v2 PLAN-CONCIERGE-FACTORY §4.7, §6.3, §9.2).
 *
 * Named annotators (CONCIERGE_ANNOTATORS) read redacted turns and annotate them; every other admin sees
 * aggregates only. Training exports are for a super admin who is also an annotator. apbe enforces all of
 * it (403); this page only hides what the API would refuse. Turn text is redacted and shown as text. */

const TABS = ['turns', 'metrics', 'evals', 'exports'];
const FILTER_LABELS = {
  thumbs_down: 'thumbs down',
  rewritten: 'rewritten',
  trimmed: 'trimmed',
  refused: 'refused',
  omission_appended: 'omission appended',
  facts_only: 'facts only',
  handoff: 'hand-off',
  changed_outcome_false: 'changed_outcome = false',
  kb_gap: 'KB gap',
  unannotated: 'unannotated',
  unannotated_by_me: 'not annotated by me',
  thumbs_up: 'thumbs up',
  errors: 'errors',
  member: 'members',
  staff: 'staff',
};
const PAGE = 50;

export function GroundingBadge({ g }) {
  if (!g) return <span className="in-muted">—</span>;
  const cls = g.status === 'passed' ? 'ok' : g.status === 'refused' ? 'bad' : 'warn';
  return (
    <span className="in-row">
      <span className={`in-badge ${cls}`}>{g.status}</span>
      {Number(g.omissionAppended) > 0 && <span className="in-badge warn">omission</span>}
      {Number(g.dropped) > 0 && <span className="in-muted">dropped {g.dropped}</span>}
    </span>
  );
}

function TurnsTab({ viewer }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => (params.get('filter') || '').split(',').filter((f) => FILTER_LABELS[f]), [params]);
  const q = params.get('q') || '';
  const since = params.get('since') || '';
  const until = params.get('until') || '';
  const mine = params.get('mine') === '1';
  const offset = Number(params.get('offset')) || 0;
  const [qDraft, setQDraft] = useState(q);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === '' || v == null || v === false) next.delete(k);
      else next.set(k, v === true ? '1' : String(v));
    }
    if (!('offset' in patch)) next.delete('offset');
    next.set('tab', 'turns');
    setParams(next, { replace: true });
  };
  const toggle = (f) => setParam({ filter: (filters.includes(f) ? filters.filter((x) => x !== f) : [...filters, f]).join(',') });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await conciergeReviewAPI.turns({ filter: filters.join(',') || undefined, q: q || undefined, since: since || undefined,
        until: until || undefined, mine: mine ? '1' : undefined, limit: PAGE, offset });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filters, q, since, until, mine, offset]);
  useEffect(() => { if (viewer && viewer.annotator) load(); }, [load, viewer]);

  if (!viewer) return null;
  if (!viewer.annotator) {
    return <div className="in-card"><div className="in-empty">Concierge turns are for named annotators (CONCIERGE_ANNOTATORS). Other admins see the metrics and evals.</div></div>;
  }
  const total = data ? data.total : 0;
  return (
    <div className="in-card">
      <div className="in-chips" style={{ marginBottom: 10 }}>
        {Object.keys(FILTER_LABELS).map((f) => (
          <button key={f} type="button" className={`in-chip ${filters.includes(f) ? 'on' : ''}`} onClick={() => toggle(f)}>{FILTER_LABELS[f]}</button>
        ))}
      </div>
      <form className="in-row" style={{ marginBottom: 10 }} onSubmit={(e) => { e.preventDefault(); setParam({ q: qDraft.trim() }); }}>
        <input className="in-input" placeholder="Search the redacted question" value={qDraft} onChange={(e) => setQDraft(e.target.value)} style={{ minWidth: 260 }} />
        <label className="in-muted">from <input className="in-input" type="date" value={since} onChange={(e) => setParam({ since: e.target.value })} /></label>
        <label className="in-muted">before <input className="in-input" type="date" value={until} onChange={(e) => setParam({ until: e.target.value })} /></label>
        <label className="in-radio"><input type="checkbox" checked={mine} onChange={(e) => setParam({ mine: e.target.checked })} /> annotated by me</label>
        <span className="in-spacer" />
        <button type="submit" className="in-btn small">Search</button>
        <button type="button" className="in-btn small" onClick={load}>Refresh</button>
      </form>
      {error && <div className="in-banner bad">{error}</div>}
      {loading && !data ? <div className="in-empty">Loading turns…</div> : !data || !data.items.length ? <div className="in-empty">No turns match.</div> : (
        <>
          <div className="in-table-wrap">
            <table className="in-table">
              <thead>
                <tr><th>#</th><th>When</th><th>Member</th><th>Question (redacted)</th><th>Kind</th><th>Grounding</th><th>Signals</th><th>Annotations</th></tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="clickable" onClick={() => navigate(`/intelligence/concierge/turns/${t.id}`)}>
                    <td className="in-mono">{t.id}</td>
                    <td className="in-muted">{fmtDate(t.created_at)}</td>
                    <td className="in-mono">{t.member}{t.viewer_class === 'staff' && <div><span className="in-badge">staff</span></div>}</td>
                    <td style={{ maxWidth: 420 }}>{t.question}</td>
                    <td>
                      <span className={`in-badge ${t.status === 'done' ? '' : 'bad'}`}>{t.kind || t.status}</span>
                      {t.error_code && <div className="in-muted">{t.error_code}</div>}
                    </td>
                    <td><GroundingBadge g={t.grounding} /></td>
                    <td className="in-muted">
                      {t.rating != null && <div>{t.rating < 0 ? 'thumbs down' : 'thumbs up'}</div>}
                      {t.plan_round > 0 && <div>asked (round {t.plan_round}){t.changed_outcome_false ? ' · changed_outcome = false' : ''}</div>}
                      {t.training_consent && <div>training consent</div>}
                    </td>
                    <td>
                      {t.annotations ? <span className="in-badge">{t.annotations}</span> : <span className="in-muted">none</span>}
                      {t.my_verdict && <div><span className={`in-badge ${t.my_verdict}`}>you: {t.my_verdict}</span></div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="in-pager">
            <span className="in-muted">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
            <button type="button" className="in-btn small" disabled={offset === 0} onClick={() => setParam({ offset: Math.max(0, offset - PAGE) || '' })}>Previous</button>
            <button type="button" className="in-btn small" disabled={offset + PAGE >= total} onClick={() => setParam({ offset: offset + PAGE })}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}

const arr = (x) => (Array.isArray(x) ? x : []);
function normalizeMetrics(raw) {
  const r = raw || {};
  return {
    days: r.days || 30, turns: arr(r.turns), grounding: arr(r.grounding), feedback: arr(r.feedback), verdicts: arr(r.verdicts), usage: arr(r.usage),
    latency_ms: r.latency_ms || { n: 0, p50: null, p95: null },
    plan_mode: r.plan_mode || { asked: 0, changed_outcome: 0, unchanged: 0 },
  };
}

function MetricsTab() {
  const [days, setDays] = useState(30);
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    conciergeReviewAPI.metrics(days).then((r) => { setM(normalizeMetrics(r.data.metrics)); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [days]);
  if (error) return <div className="in-banner bad">{error}</div>;
  if (!m) return <div className="in-empty">Loading metrics…</div>;
  const sum = (rows) => rows.reduce((s, r) => s + Number(r.n || 0), 0);
  const turns = sum(m.turns);
  const down = (m.feedback.find((f) => f.rating < 0) || {}).n || 0;
  const up = (m.feedback.find((f) => f.rating > 0) || {}).n || 0;
  const spend = m.usage.reduce((s, r) => s + Number(r.usd || 0), 0);
  return (
    <>
      <div className="in-row" style={{ marginBottom: 10 }}>
        <span className="in-muted">Aggregates only: no turn text, no member.</span>
        <span className="in-spacer" />
        <select className="in-select" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Window">
          {[7, 30, 90].map((d) => <option key={d} value={d}>last {d} days</option>)}
        </select>
      </div>
      <div className="in-grid3">
        <div className="in-card"><h3>Turns</h3><div className="in-kpi"><b>{turns}</b><span className="in-muted">in {m.days} days</span></div></div>
        <div className="in-card"><h3>Feedback</h3><div className="in-kpi"><b>{up} up · {down} down</b><span className="in-muted">thumbs</span></div></div>
        <div className="in-card">
          <h3>Latency</h3>
          <div className="in-kpi"><b>{m.latency_ms.p50 != null ? `${(m.latency_ms.p50 / 1000).toFixed(1)} s` : '—'}</b><span className="in-muted">p50 · p95 {m.latency_ms.p95 != null ? `${(m.latency_ms.p95 / 1000).toFixed(1)} s` : '—'} (n {m.latency_ms.n})</span></div>
        </div>
        <div className="in-card">
          <h3>By kind</h3>
          <table className="in-table"><tbody>{m.turns.map((r, i) => <tr key={i}><td>{r.viewer_class}</td><td>{r.status}</td><td>{r.kind || '—'}</td><td>{r.n}</td></tr>)}</tbody></table>
        </div>
        <div className="in-card">
          <h3>Grounding</h3>
          <table className="in-table"><tbody>{m.grounding.map((r) => <tr key={r.status || 'none'}><td>{r.status || '—'}</td><td>{r.n}</td><td className="in-muted">omission {r.omission}</td></tr>)}</tbody></table>
        </div>
        <div className="in-card">
          <h3>Plan mode</h3>
          <p className="in-muted">asked in {m.plan_mode.asked} turns · an answer changed the outcome in {m.plan_mode.changed_outcome} · did not in {m.plan_mode.unchanged}</p>
          <h3>Annotations</h3>
          <table className="in-table"><tbody>{m.verdicts.map((r) => <tr key={r.verdict}><td><span className={`in-badge ${r.verdict}`}>{r.verdict}</span></td><td>{r.n}</td></tr>)}</tbody></table>
        </div>
      </div>
      <div className="in-card">
        <h3>Spend (global, per day) · {usd(spend)}</h3>
        {!m.usage.length ? <p className="in-muted">No usage recorded.</p> : (
          <div className="in-table-wrap">
            <table className="in-table">
              <thead><tr><th>Day</th><th>Calls</th><th>In</th><th>Cache read</th><th>Out</th><th>USD</th></tr></thead>
              <tbody>{m.usage.map((r) => <tr key={r.day}><td>{r.day}</td><td>{r.calls}</td><td>{r.in_tokens}</td><td>{r.cache_read_tokens}</td><td>{r.out_tokens}</td><td>{usd(r.usd)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function EvalsTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { conciergeReviewAPI.evals().then((r) => setItems(r.data.items || [])).catch((e) => setError(errMsg(e))); }, []);
  if (error) return <div className="in-banner bad">{error}</div>;
  if (!items) return <div className="in-empty">Loading eval runs…</div>;
  return (
    <div className="in-card">
      <h3>eval:concierge runs (the deploy gate)</h3>
      {!items.length ? <div className="in-empty">No eval runs yet.</div> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>#</th><th>Started</th><th>Git</th><th>KB</th><th>Model</th><th>Cases</th><th>Safety</th><th>Behaviour</th><th>Judge</th><th>USD</th><th>Gate</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="in-mono">{r.id}</td><td className="in-muted">{fmtDate(r.started_at)}</td>
                  <td className="in-mono">{String(r.git_sha || '').slice(0, 8)}</td><td className="in-mono">{String(r.kb_version || '').slice(0, 10)}</td>
                  <td>{r.provider} · {r.model}{r.effort ? ` · ${r.effort}` : ''}</td><td>{r.cases}</td>
                  <td>{r.safety_pass}</td><td>{r.behaviour_pass}</td><td>{r.judge_mean == null ? '—' : Number(r.judge_mean).toFixed(2)}</td>
                  <td>{usd(r.usd)}</td>
                  <td><span className={`in-badge ${Number(r.gate_ok) ? 'ok' : 'bad'}`}>{Number(r.gate_ok) ? 'pass' : 'fail'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExportsTab({ viewer }) {
  const [form, setForm] = useState({ since: '', until: '', verdicts: [] });
  const [counts, setCounts] = useState(null);
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setItems((await conciergeReviewAPI.exports()).data.items || []);
    } catch (e) {
      setMsg({ bad: true, text: errMsg(e) });
    }
  }, []);
  useEffect(() => { if (viewer && viewer.exporter) load(); }, [load, viewer]);
  if (!viewer) return null;
  if (!viewer.exporter) {
    return <div className="in-card"><div className="in-empty">Training exports need a super admin who is also a named annotator (CONCIERGE_ANNOTATORS).</div></div>;
  }
  const run = async (preview) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await conciergeReviewAPI.createExport({ since: form.since || undefined, until: form.until || undefined,
        verdicts: form.verdicts.length ? form.verdicts : undefined, preview });
      setCounts(r.data.counts);
      if (r.data.export) setMsg({ text: `Export ${r.data.export.id}: ${r.data.export.rows} rows, expires ${fmtDate(r.data.export.expires_at)}.` });
      else if (!preview) setMsg({ bad: true, text: 'Nothing to export: no turn passed every rule.' });
      await load();
    } catch (e) {
      setMsg({ bad: true, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  const download = async (x) => {
    try {
      const r = await conciergeReviewAPI.downloadExport(x.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `concierge-export-${x.id}.jsonl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMsg({ bad: true, text: errMsg(e, 'Download failed') });
    }
  };
  const toggleVerdict = (v) => setForm((f) => ({ ...f, verdicts: f.verdicts.includes(v) ? f.verdicts.filter((x) => x !== v) : [...f.verdicts, v] }));
  return (
    <>
      <div className="in-card">
        <h3>New training export</h3>
        <p className="in-muted">
          Members only; training consent at the turn and model_improvement consent still granted; finished, annotated turns whose facts are all
          signed and member-visible. Redacted again on the way out (amounts rounded, dates shifted, pseudonymous members). The k ≥ 5 guard
          fails closed: until v3 records a quasi-identifier combination per turn, every row is dropped.
        </p>
        {msg && <div className={`in-banner ${msg.bad ? 'bad' : 'info'}`}>{msg.text}</div>}
        <div className="in-row">
          <label className="in-muted">from <input className="in-input" type="date" value={form.since} onChange={(e) => setForm((f) => ({ ...f, since: e.target.value }))} /></label>
          <label className="in-muted">before <input className="in-input" type="date" value={form.until} onChange={(e) => setForm((f) => ({ ...f, until: e.target.value }))} /></label>
          <span className="in-muted">verdicts:</span>
          {['correct', 'partly', 'wrong', 'unsafe'].map((v) => (
            <label key={v} className="in-radio"><input type="checkbox" checked={form.verdicts.includes(v)} onChange={() => toggleVerdict(v)} />{v}</label>
          ))}
          <span className="in-spacer" />
          <button type="button" className="in-btn" disabled={busy} onClick={() => run(true)}>Preview counts</button>
          <button type="button" className="in-btn primary" disabled={busy} onClick={() => run(false)}>Create export</button>
        </div>
        {counts && (
          <table className="in-table" style={{ marginTop: 10 }}><tbody>
            <tr><td>Candidates (members, consented, done, annotated)</td><td>{counts.candidates}</td></tr>
            <tr><td>Dropped: a fact not signed or not member-visible</td><td>{counts.unsigned_facts_dropped}</td></tr>
            <tr><td>Dropped: verdict filter</td><td>{counts.verdict_filtered}</td></tr>
            <tr><td>Dropped: k-anonymity ({counts.k_guard})</td><td>{counts.k_anonymity_dropped}</td></tr>
            <tr><td><b>Rows</b></td><td><b>{counts.rows}</b></td></tr>
          </tbody></table>
        )}
      </div>
      <div className="in-card">
        <h3>Exports (kept 90 days)</h3>
        {!items.length ? <div className="in-empty">No exports.</div> : (
          <table className="in-table">
            <thead><tr><th>#</th><th>Created</th><th>By</th><th>Rows</th><th>SHA-256</th><th>Expires</th><th /></tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id}>
                  <td className="in-mono">{x.id}</td><td className="in-muted">{fmtDate(x.created_at)}</td><td>{x.admin_name || `admin ${x.admin_id}`}</td>
                  <td>{x.rows_count}</td><td className="in-mono">{String(x.sha256 || '').slice(0, 16)}…</td><td className="in-muted">{fmtDate(x.expires_at)}</td>
                  <td>{x.expired ? <span className="in-badge">expired</span> : <button type="button" className="in-btn small" onClick={() => download(x)}>Download</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default function IntelligenceConcierge() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'turns';
  const [viewer, setViewer] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    intelligenceMe().then((r) => setViewer(r.data.viewer)).catch((e) => setError(errMsg(e)));
  }, []);
  const go = (t) => setParams({ tab: t }, { replace: true });
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">CONCIERGE REVIEW</h1>
            <p className="page-subtitle">Redacted concierge turns for named annotators: verdicts, corrected answers in placeholder form, KB gaps.</p>
          </div>
          <RoleChips viewer={viewer} />
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        <div className="in-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`in-tab ${tab === t ? 'active' : ''}`} onClick={() => go(t)}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
        {tab === 'turns' && <TurnsTab viewer={viewer} />}
        {tab === 'metrics' && <MetricsTab />}
        {tab === 'evals' && <EvalsTab />}
        {tab === 'exports' && <ExportsTab viewer={viewer} />}
      </main>
    </div>
  );
}
